import { useState, type ChangeEvent, type FormEvent } from 'react';
import { CONTACT, buildWhatsAppUrl } from '../../data/site';
import { MEDICAL_NEGLIGENCE_WHATSAPP_MESSAGE } from '../../data/medicalNegligenceLanding';
import { Icon } from '../Icon';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const MIN_NAME_LENGTH = 2;
const MIN_MESSAGE_LENGTH = 12;

type CareSetting =
  | ''
  | 'private_clinic'
  | 'private_doctor'
  | 'public_center'
  | 'other'
  | 'unsure';

const CARE_SETTING_LABELS: Record<Exclude<CareSetting, ''>, string> = {
  private_clinic: 'Clínica privada',
  private_doctor: 'Médico particular',
  public_center: 'Hospital o centro público',
  other: 'Otro',
  unsure: 'No estoy seguro',
};

type FormState = {
  name: string;
  phone: string;
  careSetting: CareSetting;
  message: string;
};

const INITIAL_FORM: FormState = {
  name: '',
  phone: '',
  careSetting: '',
  message: '',
};

export function MedicalNegligenceLeadForm() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [attempted, setAttempted] = useState(false);

  const nameIsValid = form.name.trim().length >= MIN_NAME_LENGTH;
  const careSettingIsValid = form.careSetting !== '';
  const messageIsValid = form.message.trim().length >= MIN_MESSAGE_LENGTH;
  const formIsValid = nameIsValid && careSettingIsValid && messageIsValid;

  const updateField =
    (field: keyof FormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      }));
    };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);

    if (!formIsValid || !form.careSetting) return;

    const lines = [
      MEDICAL_NEGLIGENCE_WHATSAPP_MESSAGE,
      `Mi nombre es ${form.name.trim()}.`,
      form.phone.trim()
        ? `Mi teléfono de contacto es ${form.phone.trim()}.`
        : '',
      `Lugar de la atención médica: ${CARE_SETTING_LABELS[form.careSetting]}.`,
      `Esto fue lo que ocurrió: ${form.message.trim()}`,
    ].filter(Boolean);

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'form_to_whatsapp',
      form_name: 'medical_negligence_lead_form',
      page_path: window.location.pathname,
    });

    window.open(
      buildWhatsAppUrl(lines.join('\n')),
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <form
      className="medical-lead-form"
      onSubmit={submit}
      noValidate
      data-analytics-form="medical_negligence_lead"
      data-analytics-event={formIsValid ? 'form_submit' : undefined}
    >
      <div className="medical-lead-form__heading">
        <p className="eyebrow">Primer contacto</p>

        <h2 id="medical-lead-form-title">
          Cuéntanos tu caso
        </h2>

        <p>
          Cuéntanos brevemente qué ocurrió. Si te resulta más cómodo,
          también puedes abrir WhatsApp y enviarnos un audio o un breve
          documento para esta primera revisión.
        </p>

        <ul
          className="medical-lead-form__contact-options"
          aria-label="Formas de contarnos tu caso por WhatsApp"
        >
          <li>
            <Icon name="mail" size={17} /> Mensaje de texto
          </li>
          <li>
            <Icon name="whatsapp" size={17} /> Audio por WhatsApp
          </li>
          <li>
            <Icon name="document" size={17} /> Breve documento
          </li>
        </ul>
      </div>

      <label className="medical-field">
        <span>Nombre y apellido *</span>

        <input
          name="name"
          value={form.name}
          onChange={updateField('name')}
          autoComplete="name"
          placeholder="Escribe tu nombre"
          required
          minLength={MIN_NAME_LENGTH}
          aria-invalid={attempted && !nameIsValid}
          aria-describedby={
            attempted && !nameIsValid
              ? 'medical-name-error'
              : undefined
          }
        />

        {attempted && !nameIsValid ? (
          <small
            id="medical-name-error"
            role="alert"
          >
            Ingresa tu nombre para continuar.
          </small>
        ) : null}
      </label>

      <label className="medical-field">
        <span>Teléfono (opcional)</span>

        <input
          name="phone"
          value={form.phone}
          onChange={updateField('phone')}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={CONTACT.phoneDisplay}
        />
      </label>

      <label className="medical-field">
        <span>¿Dónde ocurrió la atención médica? *</span>

        <select
          name="careSetting"
          value={form.careSetting}
          onChange={updateField('careSetting')}
          required
          aria-invalid={attempted && !careSettingIsValid}
          aria-describedby={
            attempted && !careSettingIsValid
              ? 'medical-care-setting-error'
              : undefined
          }
        >
          <option value="" disabled>Selecciona una opción</option>
          <option value="private_clinic">Clínica privada</option>
          <option value="private_doctor">Médico particular</option>
          <option value="public_center">Hospital o centro público</option>
          <option value="other">Otro</option>
          <option value="unsure">No estoy seguro</option>
        </select>

        {attempted && !careSettingIsValid ? (
          <small id="medical-care-setting-error" role="alert">
            Selecciona dónde ocurrió la atención para continuar.
          </small>
        ) : null}
      </label>

      <label className="medical-field">
        <span>Cuéntanos brevemente qué ocurrió *</span>

        <textarea
          name="message"
          value={form.message}
          onChange={updateField('message')}
          rows={5}
          placeholder="Describe la atención recibida y qué consecuencia tuvo para ti o tu familiar."
          required
          minLength={MIN_MESSAGE_LENGTH}
          aria-invalid={attempted && !messageIsValid}
          aria-describedby={
            attempted && !messageIsValid
              ? 'medical-message-error'
              : undefined
          }
        />

        {attempted && !messageIsValid ? (
          <small
            id="medical-message-error"
            role="alert"
          >
            Incluye una descripción breve de lo ocurrido.
          </small>
        ) : null}
      </label>

      <p className="medical-lead-form__service-note">
        Contarnos inicialmente tu caso no implica una asesoría profesional
        completa. Si se requiere una reunión, evaluación detallada u otro
        servicio profesional, te informaremos previamente.
      </p>

      <p className="medical-lead-form__privacy">
        Al continuar, compartirás esta información mediante WhatsApp. Evita
        incluir información clínica sensible que no sea necesaria para este
        primer contacto. Consulta nuestra{' '}
        <a
          href="/politica-de-privacidad"
          target="_blank"
          rel="noopener noreferrer"
        >
          Política de privacidad
        </a>.
      </p>

      <button
        className="campaign-button campaign-button--gold medical-lead-form__submit"
        type="submit"
        data-analytics-event={
          formIsValid ? 'form_to_whatsapp' : undefined
        }
      >
        <Icon name="whatsapp" size={20} />
        Abrir WhatsApp y contar mi caso
      </button>
    </form>
  );
}